import { FC, useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Stack,
  Box,
  IconButton,
  Typography,
  Chip,
  Divider,
  Tooltip,
  Button,
  FormControl,
  Select,
  MenuItem,
  Pagination
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import { dateFormat, SsccStatus, timeFormat } from '../../../common';
import { ReceiptEventPackageProps, SSCCProps } from '../../../datastore/ssccs';
import { ASNOccurrences, SsccActionsContext, SsccActionsPayload, SSCCOccurrences } from '../utils/types';
import { ManifestLegProps, ManifestSSCCProps } from '../../../datastore/tripSheets';
import { AsnStatus, TripLegStatus } from '../../../common/status';
import { buildAsnKey } from '../utils/manifestUtils';

interface SsccActionsDrawerProps {
  open: boolean;
  onClose: () => void;
  ssccs: SSCCProps[];
  ssccActionsContext: SsccActionsContext | null;
  ssccActionsPayload: SsccActionsPayload | null;
  asnOccurrences: ASNOccurrences;
  ssccOccurrences: SSCCOccurrences;
  handleOpenAddASNToSSCCDialog: () => void;
  handleOpenMarkCompletedSSCCDialog: () => void;
  handleOpenDeleteUnassignedSSCCDialog: () => void;
  handleOpenDeleteSSCCDialog: () => void;
  handleOpenDeleteASNDialog: (asn: ReceiptEventPackageProps) => void;
  getTimeBasedOnStatus: (params: { status: SsccStatus; sscc: SSCCProps | ManifestSSCCProps }) => string;
}

export const SsccActionsDrawer: FC<SsccActionsDrawerProps> = ({
  open,
  onClose,
  ssccs,
  ssccActionsContext,
  ssccActionsPayload,
  asnOccurrences,
  ssccOccurrences,
  handleOpenAddASNToSSCCDialog,
  handleOpenMarkCompletedSSCCDialog,
  handleOpenDeleteUnassignedSSCCDialog,
  handleOpenDeleteSSCCDialog,
  handleOpenDeleteASNDialog,
  getTimeBasedOnStatus
}) => {
  type AsnSortKey = 'asn' | 'po' | 'status' | 'supplier' | 'dateReceived';

  const [asnSortKey, setAsnSortKey] = useState<AsnSortKey>('asn');

  const livePayloadSscc = useMemo(
    () => (ssccActionsPayload?.ssccId ? ssccs.find((item) => item.id === ssccActionsPayload.ssccId) : undefined),
    [ssccActionsPayload?.ssccId, ssccs]
  );

  const ssccDetails = ssccActionsContext?.ssccEntry?.sscc || livePayloadSscc || ssccActionsPayload?.sscc;

  const ssccTypeLabel = ssccDetails?.isLargeItem ? 'Large' : ssccDetails ? 'Not Large' : 'N/A';

  // Get ASNs from context or directly from SSCC details for unassigned SSCCs
  const ssccAsns = useMemo(
    () =>
      ssccActionsContext?.ssccEntry?.asns ||
      ssccDetails?.receiptedEvents
        ?.filter(({ packages }: { packages: ReceiptEventPackageProps[] }) => packages.length)
        .flatMap(({ packages }: { packages: ReceiptEventPackageProps[] }) => packages) ||
      [],
    [ssccActionsContext?.ssccEntry?.asns, ssccDetails?.receiptedEvents]
  );

  // Check if drawer is opened from unassigned SSCC (no trip/leg/manifest context)
  const isUnassignedSSCC =
    ssccActionsPayload && !ssccActionsPayload.tripId && !ssccActionsPayload.legId && !ssccActionsPayload.manifestId;

  const ssccActionsDisabled = isUnassignedSSCC
    ? false
    : !ssccActionsContext ||
      ![TripLegStatus.arrived, TripLegStatus.completed].includes(ssccActionsContext.tripDisplayStatus);

  const addAsnDisabled = isUnassignedSSCC || ssccActionsDisabled;

  const deleteSsccDisabled = isUnassignedSSCC
    ? true
    : !ssccActionsContext || ssccActionsContext.tripDisplayStatus !== TripLegStatus.arrived;

  const sortedSsccAsns = useMemo(() => {
    const getSortValue = (asn: ReceiptEventPackageProps) => {
      switch (asnSortKey) {
        case 'asn':
          return (asn.asn || '').toLowerCase();
        case 'po':
          return (asn.po || '').toLowerCase();
        case 'status':
          return (asn.status || '').toLowerCase();
        case 'supplier':
          return (asn.supplierName || '').toLowerCase();
        case 'dateReceived':
          return dayjs(asn.dateReceived).isValid() ? dayjs(asn.dateReceived).valueOf() : 0;
        default:
          return '';
      }
    };

    return [...(ssccAsns || [])].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return bValue - aValue;
      }

      return String(aValue).localeCompare(String(bValue));
    });
  }, [ssccAsns, asnSortKey]);

  // ASN Paging
  const ASN_PAGE_SIZE = 10;
  const [asnPage, setAsnPage] = useState(1);
  const asnPageCount = Math.ceil((sortedSsccAsns?.length || 0) / ASN_PAGE_SIZE);
  const pagedSsccAsns = useMemo(
    () => sortedSsccAsns.slice((asnPage - 1) * ASN_PAGE_SIZE, asnPage * ASN_PAGE_SIZE),
    [sortedSsccAsns, asnPage]
  );

  useEffect(() => {
    const safePageCount = Math.max(asnPageCount, 1);
    if (asnPage > safePageCount) {
      setAsnPage(1);
    }
  }, [asnPage, asnPageCount]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 560,
          maxWidth: '100vw',
          p: 2.5
        }
      }}>
      <Stack sx={{ height: '100%' }}>
        <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, pb: 1 }}>
          <Stack spacing={2.5}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: '#f0f6fd',
                border: '1px solid rgba(25, 118, 210, 0.12)',
                position: 'sticky',
                top: 0,
                zIndex: 10
              }}>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      size="small"
                      onClick={onClose}
                      aria-label="Close SSCC drawer"
                      sx={{
                        p: 0.5,
                        width: 28,
                        height: 28,
                        border: '1px solid rgba(25, 118, 210, 0.35)',
                        backgroundColor: 'rgba(25, 118, 210, 0.12)',
                        color: '#1565c0',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.2)',
                          borderColor: 'rgba(21, 101, 192, 0.55)'
                        },
                        '&:focus-visible': {
                          outline: '2px solid rgba(21, 101, 192, 0.35)',
                          outlineOffset: 1
                        }
                      }}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color="primary"
                        sx={{ ml: 0.5, fontSize: '1.1rem', lineHeight: 1.1 }}>
                        {ssccActionsContext?.ssccEntry?.ssccId || ssccActionsPayload?.ssccId || 'N/A'}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>
                    {ssccDetails?.nickname || 'N/A'}
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack spacing={0.75} direction="row" alignItems="center">
                    <Chip
                      label={ssccDetails?.status || 'Unknown Status'}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        backgroundColor: 'rgba(0,0,0,0.06)',
                        color: 'text.secondary'
                      }}
                    />
                    {ssccDetails?.status && (
                      <Typography variant="caption" sx={{ fontSize: '0.64rem', color: 'text.secondary' }}>
                        {(() => {
                          const statusDate = getTimeBasedOnStatus({
                            status: ssccDetails.status as SsccStatus,
                            sscc: ssccDetails
                          });
                          return statusDate || 'N/A';
                        })()}
                      </Typography>
                    )}
                  </Stack>
                  <Chip
                    label={ssccTypeLabel}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      backgroundColor: 'rgba(25, 118, 210, 0.12)',
                      color: '#1565c0'
                    }}
                  />
                </Stack>

                <Divider sx={{ borderColor: 'rgba(25, 118, 210, 0.16)' }} />

                <Stack direction="row" spacing={1.25} alignItems="flex-start" justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                        fontSize: '0.58rem',
                        lineHeight: 1.1
                      }}>
                      Trip
                    </Typography>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.78rem', lineHeight: 1.2 }}>
                      {ssccActionsContext?.trip.reference || ssccActionsContext?.trip.id || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                        fontSize: '0.58rem',
                        lineHeight: 1.1
                      }}>
                      Leg
                    </Typography>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.78rem', lineHeight: 1.2 }}>
                      {ssccActionsContext
                        ? (() => {
                            const legIndex = ssccActionsContext.trip.legs.findIndex(
                              (l: ManifestLegProps) => l.id === ssccActionsContext.leg.id
                            );
                            return legIndex !== -1 ? `Leg ${legIndex + 1}` : 'N/A';
                          })()
                        : 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                        fontSize: '0.58rem',
                        lineHeight: 1.1
                      }}>
                      Manifest
                    </Typography>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.78rem', lineHeight: 1.2 }}>
                      {ssccActionsContext?.manifest.id || 'N/A'}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1.25} alignItems="flex-start" justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                        fontSize: '0.58rem',
                        lineHeight: 1.1
                      }}>
                      Weight
                    </Typography>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.78rem', lineHeight: 1.2 }}>
                      {ssccDetails?.weight || 'N/A'} kg
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                        fontSize: '0.58rem',
                        lineHeight: 1.1
                      }}>
                      Dimensions (W x H x D)
                    </Typography>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: '0.78rem', lineHeight: 1.2 }}>
                      {ssccDetails?.width || 'N/A'} x {ssccDetails?.height || 'N/A'} x {ssccDetails?.depth || 'N/A'} cm
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Box>

            {(() => {
              const ssccId = ssccActionsContext?.ssccEntry?.ssccId || ssccActionsPayload?.ssccId;
              const ssccDuplicateInfo = ssccId ? (ssccOccurrences.get(ssccId) ?? []) : [];
              const isSsccDuplicate = ssccDuplicateInfo.length > 1;

              return isSsccDuplicate ? (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(211, 47, 47, 0.06)',
                    border: '1px solid rgba(211, 47, 47, 0.2)'
                  }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ErrorOutlineIcon
                        sx={{
                          fontSize: 18,
                          color: '#c62828'
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: '#c62828',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                        Duplicate Detected • {ssccDuplicateInfo.length} occurrences
                      </Typography>
                    </Stack>
                    <Stack spacing={0.75}>
                      {ssccDuplicateInfo.map((info, ssccDuplicateInfoIndex) => (
                        <Chip
                          key={ssccDuplicateInfoIndex}
                          label={`Trip ${info.tripId || 'N/A'} • Leg ${
                            Number.isInteger(info.legIndex) ? (info.legIndex ?? 0) + 1 : 'N/A'
                          } • Manifest ${info.manifestId || 'N/A'}`}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            backgroundColor: 'rgba(211, 47, 47, 0.1)',
                            color: '#b71c1c',
                            border: '1px solid rgba(211, 47, 47, 0.25)',
                            justifyContent: 'flex-start',
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              ) : null;
            })()}

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: '#ffffff',
                border: '1px solid rgba(0,0,0,0.08)',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0
              }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexShrink: 0 }}>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  ASNs
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 125 }}>
                    <Select
                      value={asnSortKey}
                      onChange={(event) => setAsnSortKey(event.target.value as AsnSortKey)}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            '& .MuiMenuItem-root': {
                              fontSize: '0.72rem',
                              minHeight: 30,
                              py: 0.5
                            }
                          }
                        }
                      }}
                      sx={{
                        height: 24,
                        fontSize: '0.68rem',
                        '& .MuiSelect-select': {
                          fontSize: '0.68rem',
                          py: 0.25
                        },
                        '& .MuiSelect-icon': {
                          fontSize: '1rem'
                        }
                      }}>
                      <MenuItem value="asn">Sort: ASN</MenuItem>
                      <MenuItem value="po">Sort: PO</MenuItem>
                      <MenuItem value="status">Sort: Status</MenuItem>
                      <MenuItem value="supplier">Sort: Supplier</MenuItem>
                      <MenuItem value="dateReceived">Sort: Date Received</MenuItem>
                    </Select>
                  </FormControl>
                  <Chip
                    label={`Showing ${pagedSsccAsns.length === 0 ? 0 : (asnPage - 1) * ASN_PAGE_SIZE + 1}${
                      pagedSsccAsns.length > 1 ? `-${(asnPage - 1) * ASN_PAGE_SIZE + pagedSsccAsns.length}` : ''
                    } of ${sortedSsccAsns.length}`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      color: 'text.secondary'
                    }}
                  />
                </Stack>
              </Stack>
              {ssccAsns?.length ? (
                <>
                  <Stack
                    spacing={1}
                    sx={{
                      mt: 1.5,
                      flex: 1,
                      overflowY: 'auto',
                      pr: 0.5,
                      minHeight: 0
                    }}>
                    {pagedSsccAsns.map((asn, asnIndex) => {
                      const asnKey = buildAsnKey(asn);
                      const asnDuplicateInfo = asnKey ? (asnOccurrences.get(asnKey) ?? []) : [];
                      const isAsnDuplicate = asnDuplicateInfo.length > 1;
                      const isAsnAvailable =
                        (asn.status || '').toString().trim().toLowerCase() === AsnStatus.available.toLowerCase();
                      const canShowDeleteAsnAction =
                        isAsnDuplicate ||
                        (!isAsnAvailable &&
                          !isUnassignedSSCC &&
                          !!ssccActionsContext &&
                          [TripLegStatus.arrived, TripLegStatus.completed].includes(
                            ssccActionsContext.tripDisplayStatus
                          ));

                      return (
                        <Box
                          key={`${asn.asn}-${asnIndex + (asnPage - 1) * ASN_PAGE_SIZE}`}
                          sx={{
                            borderRadius: 1,
                            backgroundColor: 'rgba(0,0,0,0.03)',
                            border: isAsnDuplicate ? '2px solid #d32f2f' : '1px solid rgba(0,0,0,0.08)'
                          }}>
                          {isAsnDuplicate && (
                            <Box
                              sx={{
                                backgroundColor: 'rgba(211, 47, 47, 0.06)',
                                borderBottom: '1px solid rgba(211, 47, 47, 0.2)',
                                px: 1.5,
                                py: 0.75
                              }}>
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <ErrorOutlineIcon
                                  sx={{
                                    fontSize: 14,
                                    color: '#c62828'
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    color: '#c62828',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                  }}>
                                  Duplicate • {asnDuplicateInfo.length} locations
                                </Typography>
                              </Stack>
                              <Stack spacing={0.25} sx={{ mt: 0.75, pl: 2.5 }}>
                                {asnDuplicateInfo.map((info, idx) => (
                                  <Typography
                                    key={`${info.tripId}-${info.legIndex}-${info.manifestId}-${idx}`}
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.65rem',
                                      color: '#b71c1c',
                                      lineHeight: 1.4
                                    }}>
                                    T{info.tripId || '?'} / L
                                    {Number.isInteger(info.legIndex) ? (info.legIndex || 0) + 1 : '?'} / M
                                    {info.manifestId || '?'} / S{info.ssccId || '?'}
                                  </Typography>
                                ))}
                              </Stack>
                            </Box>
                          )}
                          <Box sx={{ px: 1.5, py: 1 }}>
                            <Stack spacing={1}>
                              {/* ASN and PO Row */}
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                                  <Stack sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                      ASN
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight={700}
                                      sx={{
                                        wordBreak: 'break-word',
                                        color: 'primary.main'
                                      }}>
                                      {asn.asn || 'N/A'}
                                    </Typography>
                                  </Stack>
                                  {asn.po && (
                                    <>
                                      <Divider orientation="vertical" flexItem />
                                      <Stack sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                                          PO
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          fontWeight={700}
                                          sx={{
                                            wordBreak: 'break-word',
                                            color: '#1976d2'
                                          }}>
                                          {asn.po}
                                        </Typography>
                                      </Stack>
                                    </>
                                  )}
                                </Stack>
                                {canShowDeleteAsnAction && (
                                  <Tooltip
                                    title={
                                      ssccActionsDisabled
                                        ? `Trip status is ${ssccActionsContext?.tripDisplayStatus || 'unknown'}, please delete ASN in Bustle.`
                                        : ''
                                    }
                                    arrow>
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={ssccActionsDisabled}
                                        onClick={() => handleOpenDeleteASNDialog(asn)}
                                        sx={{
                                          color: '#d32f2f',
                                          '&:hover': {
                                            backgroundColor: 'rgba(211, 47, 47, 0.08)'
                                          },
                                          '&.Mui-disabled': {
                                            color: 'rgba(0, 0, 0, 0.26)'
                                          }
                                        }}>
                                        <DeleteIcon sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </Stack>

                              {/* Company, Status, and Date Row */}
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                <Chip
                                  label={asn.status || 'Unknown Status'}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    flexShrink: 0
                                  }}
                                />
                                {asn.statusDate && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.65rem', flexShrink: 0 }}>
                                    {`${asn.statusDate?.format(dateFormat)} ${asn.statusDate?.format(timeFormat)}`}
                                  </Typography>
                                )}
                              </Stack>

                              {/* Priority Row */}
                              <Stack direction="row" alignItems="center" justifyContent="space-between">
                                {asn.priority && (
                                  <Stack>
                                    <Chip
                                      label={asn.priority}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        fontWeight: 500,
                                        width: 'fit-content',
                                        borderColor:
                                          asn.priority === 'High'
                                            ? '#f44336'
                                            : asn.priority === 'Medium'
                                              ? '#ff9800'
                                              : '#2196f3'
                                      }}
                                    />
                                  </Stack>
                                )}
                                {asn.shipToAttention && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      fontSize: '0.7rem'
                                    }}>
                                    {asn.shipToAttention}
                                  </Typography>
                                )}
                              </Stack>

                              {/* Supplier Name Row */}
                              {asn.supplierName && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: '0.7rem'
                                  }}>
                                  {asn.supplierName}
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ mt: 1.5 }}>
                  No ASNs
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            width: '100%',
            borderTop: '1px solid rgba(0,0,0,0.08)',
            zIndex: 20
          }}>
          {asnPageCount > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2, pt: 2 }}>
              <Pagination
                count={asnPageCount}
                page={asnPage}
                onChange={(_, page) => setAsnPage(page)}
                size="medium"
                color="primary"
                siblingCount={1}
                boundaryCount={2}
                sx={{ minWidth: 320 }}
              />
            </Box>
          )}
          <Stack spacing={1}>
            <Tooltip
              title={
                addAsnDisabled
                  ? isUnassignedSSCC
                    ? 'You cannot add ASN to an unassigned SSCC, but you can delete non-available status ASNs from an assigned SSCC, then the ASN can be transferred here.'
                    : `Trip status is ${ssccActionsContext?.tripDisplayStatus || 'unknown'}, please add SSCC in Bustle.`
                  : ''
              }
              arrow>
              <span>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={addAsnDisabled}
                  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  onClick={handleOpenAddASNToSSCCDialog}
                  sx={{
                    height: 36,
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    textTransform: 'none',
                    borderColor: 'rgba(25, 118, 210, 0.6)',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#1976d2',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    },
                    backgroundColor: '#ffffff'
                  }}>
                  Add ASN to SSCC
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                ssccActionsDisabled
                  ? `Trip status is ${ssccActionsContext?.tripDisplayStatus || 'unknown'}, you can not mark completed.`
                  : ''
              }
              arrow>
              <span>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={ssccActionsDisabled}
                  startIcon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                  onClick={handleOpenMarkCompletedSSCCDialog}
                  sx={{
                    height: 36,
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    textTransform: 'none',
                    borderColor: 'rgba(76, 175, 80, 0.6)',
                    color: '#4caf50',
                    '&:hover': {
                      borderColor: '#4caf50',
                      backgroundColor: 'rgba(76, 175, 80, 0.04)'
                    },
                    backgroundColor: '#ffffff'
                  }}>
                  Mark SSCC Completed
                </Button>
              </span>
            </Tooltip>
            {isUnassignedSSCC && (
              <Tooltip
                title="Deletes this unassigned SSCC using the unassigned flow (no Receive Manifest option) and logs to history."
                arrow>
                <span>
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={ssccActionsDisabled}
                    startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
                    onClick={handleOpenDeleteUnassignedSSCCDialog}
                    sx={{
                      height: 36,
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      borderColor: 'rgba(211, 47, 47, 0.6)',
                      color: '#c62828',
                      '&:hover': {
                        borderColor: '#c62828',
                        backgroundColor: 'rgba(211, 47, 47, 0.06)'
                      },
                      backgroundColor: '#ffffff'
                    }}>
                    Delete SSCC
                  </Button>
                </span>
              </Tooltip>
            )}
            {!isUnassignedSSCC && (
              <Tooltip
                title={
                  deleteSsccDisabled
                    ? ssccActionsContext?.tripDisplayStatus === TripLegStatus.completed
                      ? 'Trip status is Completed, deleting SSCC is disabled.'
                      : `Trip status is ${ssccActionsContext?.tripDisplayStatus || 'unknown'}, please delete SSCC in Bustle.`
                    : ''
                }
                arrow>
                <span>
                  <Button
                    fullWidth
                    variant="outlined"
                    disabled={deleteSsccDisabled}
                    startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
                    onClick={handleOpenDeleteSSCCDialog}
                    sx={{
                      height: 36,
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      borderColor: 'rgba(211, 47, 47, 0.6)',
                      color: '#c62828',
                      '&:hover': {
                        borderColor: '#c62828',
                        backgroundColor: 'rgba(211, 47, 47, 0.06)'
                      },
                      backgroundColor: '#ffffff'
                    }}>
                    Delete SSCC
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Box>
      </Stack>
    </Drawer>
  );
};

SsccActionsDrawer.displayName = 'SsccActionsDrawer';
